import {
  SENTIMENT,
  Sentiment as SentimentType,
  USER_ACCESS,
} from "@/constants";
import { useUser } from "@/hooks/useUser";
import { User } from "@/types/User";
import { renderTemplateWithData } from "@/utils";
import Sentiment, { SentimentOptions } from "@components/Sentiment";
import Tooltip from "@components/Tooltip";
import { i18n } from "@lingui/core";

import { useEpisode } from "../../context";

const SENTIMENT_PLURAL_MAP = {
  [SENTIMENT.VERY_POSITIVE]: L.MENTIONS_SENTIMENT_TONE_VERY_POSITIVE_PLURAL,
  [SENTIMENT.POSITIVE]: L.MENTIONS_SENTIMENT_TONE_POSITIVE_PLURAL,
  [SENTIMENT.NEUTRAL]: L.MENTIONS_SENTIMENT_TONE_NEUTRAL_PLURAL,
  [SENTIMENT.NEGATIVE]: L.MENTIONS_SENTIMENT_TONE_NEGATIVE_PLURAL,
  [SENTIMENT.VERY_NEGATIVE]: L.MENTIONS_SENTIMENT_TONE_VERY_NEGATIVE_PLURAL,
};

const EpisodeSentiment = () => {
  const {
    aggregated_sentiment,
    updateAggregatedSentiment,
    setSentimentActive,
    mentions,
  } = useEpisode();
  const { data: user } = useUser();
  const user_type = (user?.data as User)?.user_type;

  let text = "";
  if (mentions.length > 1) {
    const counts: Record<SentimentType, number> = mentions.reduce(
      (acc, mention) => {
        if (!mention.sentiment) return acc;
        acc[mention.sentiment] = (acc[mention.sentiment] || 0) + 1;
        return acc;
      },
      {} as Record<SentimentType, number>
    );

    const mixed = counts.positive !== 0 && counts.positive === counts.negative;
    const most = Object.entries(counts).sort((a, b) => b[1] - a[1])[0] as [
      SentimentType,
      number,
    ];
    const numbers = Object.values(counts);
    const unanimous = numbers.includes(mentions.length);

    // Nothing to show.
    if (aggregated_sentiment === null) {
      text = "";
      // All are the same.
    } else if (unanimous) {
      text = L.MENTIONS_SENTIMENT_TOOLTIP_UNANIMOUS.replace(
        "{sentiment}",
        i18n._(SENTIMENT_PLURAL_MAP[aggregated_sentiment])
      );
      // One of the sentiments have more occurences than the others.
    } else if (mixed) {
      text = L.MENTIONS_SENTIMENT_TOOLTIP_MIXED;
    } else if (most) {
      text = renderTemplateWithData(L.MENTIONS_SENTIMENT_TOOLTIP_MOST_OF_ONE, {
        total: mentions.length.toString(),
        most: most[1]?.toString(),
        sentiment: SENTIMENT_PLURAL_MAP[most[0]] || "",
      });
      // There are as many positive as negative occurences.
    }
  }

  return (
    <Tooltip passThrough={!text} content={text}>
      <Sentiment
        disabled={user_type == undefined || user_type == USER_ACCESS.READONLY}
        value={aggregated_sentiment}
        onMouseEnter={() => setSentimentActive(true)}
        onMouseLeave={() => setSentimentActive(false)}
        onChange={updateAggregatedSentiment}
      />
    </Tooltip>
  );
};

export default EpisodeSentiment;

export const MentionSentiment: React.FunctionComponent<{
  value: Nullable<SentimentType>;
  onChange: (value: SentimentType) => void;
}> = ({ value, onChange }) => {
  // Value might not be ready yet.
  if (value === null) return null;

  return <SentimentOptions value={value} onChange={onChange} />;
};
